import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import {
  subscriptionsURLs,
  serviceConfigsURLs,
  billingAccountConfigsURLs,
  billingAccountServicesURLs,
  fileManagerURLs,
} from '../../../_network/apiUrls'
import { sortByPriority } from '../_helpers/sort'
// import { calculateDateTime } from '../_helpers/dates'
import {
  setProgressStep,
  setGroup,
  setSubscriptionPackage,
  setSubscriptionRenewable,
  setOpenStackConfigs,
  setAdminUiConfigs,
  setCustomerUiConfigs,
  setBillingConfigs,
  setBillingConfigPermissions,
} from '../../../store/reducers/subscriptionAddSlice'
import { getAllowedRoles } from '../_helpers/billing'
import CustomSelectField from '../../_common/_form_fields/CustomSelectField'
import CustomBackdrop from '../../_common/CustomBackdrop'
import CustomDialog from '../../_common/CustomDialog'
import SubscriptionItem from './SubscriptionItem'
import ActiveSubscription from './ActiveSubscription'

const SubscriptionsViewWrapper = (props) => {
  const { clientSubscriptions, subscriptionGroupsData, setFetchSubscriptions } = props
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const account_id = useSelector(state => state.profile.account_id)
  const account_balance = useSelector(state => state.profile.account_balance)

  const [supportedServiceConfigs, setSupportedServiceConfigs] = useState([])
  const [subscriptionGroup, setSubscriptionGroup] = useState('')
  const [subscriptionGroupsList, setSubscriptionGroupsList] = useState([])
  const [subscriptionPackagesData, setSubscriptionPackagesData] = useState([])
  const [filteredClientSubscriptions, setFilteredClientSubscriptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false)
  const [updateSubscriptionDialogOpen, setUpdateSubscriptionDialogOpen] = useState(false)
  const [noFundsDialogOpen, setNoFundsDialogOpen] = useState(false)
  const [cancelId, setCancelId] = useState(null)
  const [updateProps, setUpdateProps] = useState(null)
  
  const pageLabelStyle = {
    color: theme.palette.vodka,
    fontSize: '18px',
    fontWeight: '500',
    padding: '0px 0px 20px 20px',
  }

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.grayX11,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.grayX11,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grayX11,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grayX11,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.grayX11,
    },
  }

  const subscriptionsWrapperStyle = {
    background: theme.palette.aliceBlue,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    padding: '20px',
    borderRadius: '20px',
  }

  const confirmButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const noActiveSubscriptionsWrapperStyle = {
    background: theme.palette.aliceBlue,
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    marginBottom: '20px',
  }

  const noActiveSubscriptionsLabelStyle = {
    height: '50px',
    background: theme.palette.violetsAreBlue,
    color: 'white',
    fontSize: '14px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const noActiveSubscriptionsTextStyle = {
    fontSize: '24px',
    textAlign: 'center',
    padding: '40px 0px',
  }

  const removeSpacesAndSpecialChars = (str) => {
    return str.replace(/[\s~`!@#$%^&*(){}[\];:"'<,>?/\\|+=-]/g, '')
  }

  const handleChangeSubscriptionGroup = (value) => {
    setSubscriptionGroup(value)
  }

  const handleNoFundsDialogOpen = () => {
    setNoFundsDialogOpen(true)
  }

  const handleNoFundsDialogClose = () => {
    setNoFundsDialogOpen(false)
  }

  const handleCancelSubscriptionDialogOpen = () => {
    setCancelSubscriptionDialogOpen(true)
  }

  const handleCancelSubscriptionDialogClose = () => {
    setCancelSubscriptionDialogOpen(false)
  }

  const handleUpdateSubscriptionDialogOpen = (totalPrice) => {
    if (totalPrice && account_balance >= totalPrice) {
      setUpdateSubscriptionDialogOpen(true)
    } else {
      handleNoFundsDialogOpen()
    }
  }

  const getServiceGroup = (service_type, service) => {
    const _service = supportedServiceConfigs.find(item => item.service_type === service_type && item.service === service)
    return _service ? _service.service_group : ''
  }

  const handleUpdateSubscriptionDialogClose = () => {
    setUpdateSubscriptionDialogOpen(false)
  }

  const uploadFileRequest = async ({ method, file, url_key }) => {
    const url = constants.core_service_domain + '/' + fileManagerURLs[url_key]

    const form_data = new FormData()

    if (method === 'DELETE') {
      form_data.append('file_url', file)
    } else {
      form_data.append('file', file, removeSpacesAndSpecialChars(file.name))
    }

    const request_data = { url, method, form_data }

    const response = await request(request_data)

    if (response && response.status_code && response.status_code < 400 && response.data && response.data.url) {
      return response.data.url
    }
  }

  const clientSubscriptionRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.core_service_domain + '/' + subscriptionsURLs.getClientSubscriptions + '/' + record_id :
    constants.core_service_domain + '/' + subscriptionsURLs.getClientSubscriptions
    
    const request_data = data ? { url, method, data } : { url, method }

    const response = await request(request_data)

    if (response.status_code && response.status_code < 400 && response.data) {
      return response.data
    }

    // if (method === 'GET') {
    //   const response = await request(request_data)
    //   if (response.status_code && response.status_code < 400 && response.data) {
    //     return response.data
    //   }
    // }
    // console.log('client_subscription', request_data)
  }

  const clientServiceConfigsRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs + '/' + record_id :
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs

    const request_data = data ? { url, method, data } : { url, method }

    const response = await request(request_data)

    if (response.status_code && response.status_code < 400 && response.data) {
      return response.data
    }

    // if (method === 'GET') {
    //   const response = await request(request_data)
    //   if (response.status_code && response.status_code < 400 && response.data) {
    //     return response.data
    //   }
    // }
    // console.log('client-service-configs', request_data)
  }

  const clientCertificateConfigsRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs + '/' + record_id :
    constants.core_service_domain + '/' + serviceConfigsURLs.getClientCertificateConfigs

    const request_data = data ? { url, method, data } : { url, method }

    const response = await request(request_data)

    if (response.status_code && response.status_code < 400 && response.data) {
      return response.data
    }
    // console.log(request_data, response)
    // if (method === 'GET') {
    //   const response = await request(request_data)
    //   if (response.status_code && response.status_code < 400 && response.data) {
    //     return response.data
    //   }
    // }
    // console.log('client-certificate-configs', request_data)
  }

  const billingAccountConfigsRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs + '/' + record_id :
    constants.billing_url + '/' + billingAccountConfigsURLs.getAccountConfigs

    const request_data = data ?
    { url, method, data, headers: { 'customer-account-id': account_id } } :
    { url, method, headers: { 'customer-account-id': account_id } }

    const response = await request(request_data)
    
    if (response.status_code && response.status_code < 400 && response.data) {
      return response.data
    }

    // if (method === 'GET') {
    //   const response = await request(request_data)
    //   if (response.status_code && response.status_code < 400 && response.data) {
    //     return response.data
    //   }
    // }
    // console.log('billing-account-configs', request_data)
  }

  const billingAccountServicesRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices + '/' + record_id :
    constants.billing_url + '/' + billingAccountServicesURLs.getAccountServices
    
    const request_data = data ?
    { url, method, data, headers: { 'customer-account-id': account_id } } :
    { url, method, headers: { 'customer-account-id': account_id } }
    
    const response = await request(request_data)

    if (response.status_code && response.status_code < 400 && response.data) {
      return response.data
    }

    // if (method === 'GET') {
    //   const response = await request(request_data)
    //   if (response.status_code && response.status_code < 400 && response.data) {
    //     return response.data
    //   }
    // }
    // console.log('billing-account-services', request_data)
  }

  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // Cancel ---------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  const cancelSubscription = async () => {
    if (!cancelId) {
      return
    }

    setIsLoading(true)
    handleCancelSubscriptionDialogClose()

    const serviceConfigs = await clientServiceConfigsRequest({ method: 'GET' })
    const certificateConfigs = await clientCertificateConfigsRequest({ method: 'GET' })
    const billingAccountConfigs = await billingAccountConfigsRequest({ method: 'GET' })
    const billingAccountServices = await billingAccountServicesRequest({ method: 'GET' })
    
    await clientSubscriptionRequest({ method: 'PUT', data: { status: 'canceled' }, record_id: cancelId })

    if (subscriptionGroup === 'openstack_admin') {
      if (serviceConfigs) {
        for (let index = 0; index < serviceConfigs.length; index++) {
          const item = serviceConfigs[index]
    
          if (item.service_type === 'frontend') {
            if (item.config_params && item.config_params.logo) {
              await uploadFileRequest({ method: 'DELETE', file: item.config_params.logo, url_key: 'getDeleteFile' })
            }
    
            if (item.config_params && item.config_params.promo_config && item.config_params.promo_config.background_image) {
              await uploadFileRequest({ method: 'DELETE', file: item.config_params.promo_config.background_image, url_key: 'getDeleteFile' })
            }
    
            if (item.config_params && item.config_params.signup_config && item.config_params.signup_config.documents) {
              for (let index2 = 0; index2 < item.config_params.signup_config.documents.length; index2++) {
                const item2 = item.config_params.signup_config.documents[index2]
                if (item2.type === 'file' && item2.value.includes('s3.grena')) {
                  await uploadFileRequest({ method: 'DELETE', file: item2.value, url_key: 'getDeleteFile' })
                }
              }
            }
          }
    
          await clientServiceConfigsRequest({ method: 'DELETE', record_id: item.id })
        }
      }
      
      if (certificateConfigs) {
        for (let index = 0; index < certificateConfigs.length; index++) {
          const item = certificateConfigs[index]
          await clientCertificateConfigsRequest({ method: 'DELETE', record_id: item.job_id })
        }
      }
  
      if (billingAccountConfigs) {
        await billingAccountConfigsRequest({ method: 'PUT', data: { status: 'deleted' } })
      }
  
      if (billingAccountServices) {
        for (let index = 0; index < billingAccountServices.length; index++) {
          const item = billingAccountServices[index]
          await billingAccountServicesRequest({ method: 'DELETE', record_id: item.id })
        }
      }
    }    
    
    setIsLoading(false)
    setFetchSubscriptions(prev => prev + 1)
    navigate('/')
  }

  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // Update ---------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  const updateSubscription = async () => {
    if (!updateProps) {
      return
    }
    
    const currentSubscription = filteredClientSubscriptions.length > 0 ? filteredClientSubscriptions[0] : null

    if (!currentSubscription) {
      const subscription_package_object = subscriptionPackagesData.find(item => item.id === updateProps.id)
      const subscription_name =  subscription_package_object ? subscription_package_object.package_name : ''

      let selected_services = []

      if (subscription_package_object && subscription_package_object.services) {
        subscription_package_object.services.forEach(s => {
          if (s.is_mandatory || updateProps.selectedOptionalServices.includes(s.service)) {
            selected_services.push(s)
          }
        })
      }

      let next_step = 3

      if (subscriptionGroup === 'isender') {
        next_step = 4
      }

      dispatch(setProgressStep({ progress_step: next_step }))

      dispatch(setGroup({ group: subscriptionGroup }))

      dispatch(setSubscriptionPackage({
        subscription_package: {
          id: updateProps.id,
          name: subscription_name,
          services: selected_services,
        }
      }))
  
      dispatch(setSubscriptionRenewable({ subscription_renewable: updateProps.renewable }))

      dispatch(setOpenStackConfigs({ openstack_configs: [] }))
      dispatch(setAdminUiConfigs({ admin_ui_configs: null }))
      dispatch(setCustomerUiConfigs({ customer_ui_configs: null }))
      dispatch(setBillingConfigs({ billing_configs: null }))
      dispatch(setBillingConfigPermissions({ billing_permissions: null }))

      navigate('/add-new')

      return
    }

    setIsLoading(true)
    handleUpdateSubscriptionDialogClose()
    
    const newSubscription = subscriptionPackagesData.find(item => item.id === updateProps.id)
    
    const new_ui_service = newSubscription.services.find(item => (item.service === 'custom_admin_ui' || item.service === 'custom_client_ui') && (item.is_mandatory || updateProps.selectedOptionalServices.includes(item.service)))

    const new_billing = newSubscription.services.find(item => item.service === 'openstack_billing' && (item.is_mandatory || updateProps.selectedOptionalServices.includes(item.service)))
    const current_billing = currentSubscription.services.find(item => item.service === 'openstack_billing')

    const serviceConfigs = await clientServiceConfigsRequest({ method: 'GET' })
    const clientCertificateConfigs = await clientCertificateConfigsRequest({ method: 'GET' })
    const billingAccountConfigs = await billingAccountConfigsRequest({ method: 'GET' })
    const billingAccountServices = await billingAccountServicesRequest({ method: 'GET' })

    // const end_time = newSubscription.duration === 'month' ? calculateDateTime('months', 'plus', 1) : calculateDateTime('years', 'plus', 1)

    let new_services = []

    newSubscription.services.forEach(item => {
      if (item.is_mandatory || updateProps.selectedOptionalServices.includes(item.service)) {
        new_services.push(item)
      }
    })
    
    const subscriptionPostData = {
      package_name: newSubscription.package_name,
      duration: newSubscription.duration,
      fee: updateProps.totalPrice,
      currency: newSubscription.currency,
      renewable: updateProps.renewable,
      title: newSubscription.title,
      services: new_services,
    }
    
    await clientSubscriptionRequest({ method: 'PUT', data: subscriptionPostData, record_id: currentSubscription.id })

    if (subscriptionGroup === 'openstack_admin') {
      if (serviceConfigs) {
        for (let index = 0; index < serviceConfigs.length; index++) {
          const item = serviceConfigs[index]
          const is_new_service = new_services.find(item2 => item2.service === item.service)
          
          if (!is_new_service || (!new_ui_service && item.service_type === 'backend')) {
            if (item.service_type === 'frontend') {
              if (item.config_params && item.config_params.logo) {
                await uploadFileRequest({ method: 'DELETE', file: item.config_params.logo, url_key: 'getDeleteFile' })
              }
      
              if (item.config_params && item.config_params.promo_config && item.config_params.promo_config.background_image) {
                await uploadFileRequest({ method: 'DELETE', file: item.config_params.promo_config.background_image, url_key: 'getDeleteFile' })
              }
      
              if (item.config_params && item.config_params.signup_config && item.config_params.signup_config.documents) {
                for (let index2 = 0; index2 < item.config_params.signup_config.documents.length; index2++) {
                  const item2 = item.config_params.signup_config.documents[index2]
                  if (item2.type === 'file' && item2.value.includes('s3.grena')) {
                    await uploadFileRequest({ method: 'DELETE', file: item2.value, url_key: 'getDeleteFile' })
                  }
                }
              }
            }
            
            await clientServiceConfigsRequest({ method: 'DELETE', record_id: item.id })
          }
        }
      }
      
      if (clientCertificateConfigs) {
        for (let index = 0; index < clientCertificateConfigs.length; index++) {
          const item = clientCertificateConfigs[index]
          await clientCertificateConfigsRequest({ method: 'DELETE', record_id: item.job_id })
        }
      }
      
      for (let index = 0; index < newSubscription.services.length; index++) {
        const item = newSubscription.services[index]
        const is_current_service = serviceConfigs ? serviceConfigs.find(item2 => item2.service === item.service) : null
  
        if (!is_current_service && (item.is_mandatory || updateProps.selectedOptionalServices.includes(item.service)) && (new_ui_service || item.service_type !== 'backend')) {
          let config_params = {}
  
          if (item.service_type === 'backend' && billingAccountServices) {
            const billing_service = billingAccountServices.find(item2 => item2.service_name === item.service)
            
            if (billing_service) {
              config_params = {
                service_domain: billing_service.service_domain,
                api_version: [billing_service.service_version],
                is_identity_provider: billing_service.is_identity_provider,
                allowed_roles: getAllowedRoles(item.service, supportedServiceConfigs),
                visible_to_service: ['custom_admin_ui', 'custom_client_ui'],
              }
  
              if (billing_service.is_identity_provider) {
                config_params.default_region = billing_service.region_name
              } else {
                config_params.region_name = billing_service.region_name
              }
            }
          }
  
          if (Object.keys(config_params).length === 0) {
            const service_config_object = supportedServiceConfigs.find(item2 => item2.service === item.service)
            if (service_config_object) {
              config_params = service_config_object.config_params
              if (!config_params.region_name) {
                const default_region_config = serviceConfigs.find(item2 => item2.config_params && item2.config_params.default_region)
                if (default_region_config && default_region_config.config_params && default_region_config.config_params.default_region) {
                  config_params.region_name = default_region_config.config_params.default_region
                }
              }
            }
          }
          
          const servicePostData = {
            service_group: getServiceGroup(item.service_type, item.service),
            service_type: item.service_type,
            service: item.service,
            title: item.title,
            config_params,
          }
          
          await clientServiceConfigsRequest({ method: 'POST', data: servicePostData })
        }
      }
  
      if (current_billing && !new_billing) {
        if (billingAccountConfigs) {
          await billingAccountConfigsRequest({ method: 'PUT', data: { status: 'deleted' } })
        }
        
        if (billingAccountServices) {
          for (let index = 0; index < billingAccountServices.length; index++) {
            const item = billingAccountServices[index]
            await billingAccountServicesRequest({ method: 'DELETE', record_id: item.id })
          }
        }
      }
  
      if (!current_billing && new_billing) {
        if (billingAccountConfigs) {
          await billingAccountConfigsRequest({ method: 'PUT', data: { status: 'inactive' } })
        } else {
          const billingAccountConfigsPostData = {
            account_id: account_id,
            multiregion_support: true,
            currency: {
              name: 'usd',
              code: '840',
            },
            payment_methods: [],
            status: 'inactive',
            default_billing_type: 'prepaid',
            postpaid_invoice: 'auto',
            postpaid_invoice_pay_period: 0,
            use_vat_tax: false,
            price_includes_vat: false,
            vat: 0,
            allow_public_price_calculator: false,
          }
    
          await billingAccountConfigsRequest({ method: 'POST', data: billingAccountConfigsPostData })
        }      
      }
    }   

    setIsLoading(false)
    setFetchSubscriptions(prev => prev + 1)
  }

  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // Renewable ------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------------------

  const updateRenewableStatus = async (updated_renewable) => {
    setIsLoading(true)

    const currentSubscription = filteredClientSubscriptions.length > 0 ? filteredClientSubscriptions[0] : null
    
    if (currentSubscription) {
      const subscriptionPostData = { renewable: updated_renewable }      
      await clientSubscriptionRequest({ method: 'PUT', data: subscriptionPostData, record_id: currentSubscription.id })
    }

    setIsLoading(false)
    setFetchSubscriptions(prev => prev + 1)
  }

  useEffect(() => {
    let subscription_groups_list = []

    subscriptionGroupsData.forEach(item => {
      subscription_groups_list.push({
        value: item.group_name,
        keyword: item.title.value,
      })
    })
    
    setSubscriptionGroupsList(subscription_groups_list)
  }, [subscriptionGroupsData])

  useEffect(() => {
    const fetchSupportedServiceConfigsData = async () => {
      const url = constants.core_service_domain + '/' + serviceConfigsURLs.getSupportedServiceConfigs
      
      const request_data = { url, method: 'GET' }
      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setSupportedServiceConfigs(response.data)
      } else {
        setSupportedServiceConfigs([])
      }
    }

    fetchSupportedServiceConfigsData()
  }, [])
  
  useEffect(() => {
    const fetchSubscriptionPackagesData = async () => {
      const url = constants.core_service_domain + '/' + subscriptionsURLs.getSubscriptionPackages      
      const request_data = { url, method: 'GET' }      
      const response = await request(request_data)
      
      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        const response_data_filtered = response.data.filter(item => item.group === subscriptionGroup)
        setSubscriptionPackagesData(sortByPriority(response_data_filtered))
      } else {
        setSubscriptionPackagesData([])
      }
    }

    fetchSubscriptionPackagesData()
  }, [subscriptionGroup])

  useEffect(() => {
    if (subscriptionGroupsList.length > 0) {
      setSubscriptionGroup(subscriptionGroupsList[0].value)
    } else {
      setSubscriptionGroup('')
    }
  }, [subscriptionGroupsList])

  useEffect(() => {
    if (clientSubscriptions.length > 0 && subscriptionGroup) {
      setFilteredClientSubscriptions(clientSubscriptions.filter(item => item.package_group === subscriptionGroup))
    } else {
      setFilteredClientSubscriptions([])
    }
  }, [clientSubscriptions, subscriptionGroup])
  
  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Box sx={{ padding: '40px 20px' }}>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, lg: 9 }}>

            <Stack
              direction='row'
              alignItems='flex-start'
              justifyContent='space-between'
              sx={{ marginBottom: '20px' }}
            >
              <Box sx={pageLabelStyle}>{defaultTexts.subscriptions}</Box>
              <Box sx={{ width: '300px' }}>
                <CustomSelectField
                  items={subscriptionGroupsList} 
                  currentValue={subscriptionGroup}
                  setCurrentValue={handleChangeSubscriptionGroup}
                  self_item_titles={true}
                  size='small'
                  sx={formControlStyle}
                  outlinedInputSx={outlinedInputStyle}
                />
              </Box>            
            </Stack>

            {
              subscriptionGroup &&
              <Box sx={subscriptionsWrapperStyle}>
                {
                  subscriptionPackagesData.map((item, index) => (
                    <SubscriptionItem
                      key={index}
                      {...item}
                      clientSubscriptions={clientSubscriptions}
                      setCancelId={setCancelId}
                      setUpdateProps={setUpdateProps}
                      handleCancelSubscriptionDialogOpen={handleCancelSubscriptionDialogOpen}
                      handleUpdateSubscriptionDialogOpen={handleUpdateSubscriptionDialogOpen}
                      updateRenewableStatus={updateRenewableStatus}
                    />
                  ))
                }
              </Box>
            }

          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            {
              filteredClientSubscriptions.length > 0 ?
              <>
                {
                  filteredClientSubscriptions.map((item, index) => (
                    <ActiveSubscription
                      key={index}
                      {...item}
                      packageData={subscriptionPackagesData.find(item2 => item2.package_name === item.package_name)}
                      setUpdateProps={setUpdateProps}
                      handleUpdateSubscriptionDialogOpen={handleUpdateSubscriptionDialogOpen}
                      setCancelId={setCancelId}
                      handleCancelSubscriptionDialogOpen={handleCancelSubscriptionDialogOpen}
                      updateRenewableStatus={updateRenewableStatus}
                    />
                  ))
                }
              </> :
              <Box sx={noActiveSubscriptionsWrapperStyle}>
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Box sx={noActiveSubscriptionsLabelStyle}>{defaultTexts.activeSubscriptions}</Box>
                </Stack>
                <Box sx={noActiveSubscriptionsTextStyle}>{defaultTexts.noActiveSubscriptions}</Box>
              </Box>
            }            
          </Grid>
        </Grid>

      </Box>

      <CustomDialog
        open={cancelSubscriptionDialogOpen}
        onClose={handleCancelSubscriptionDialogClose}
        dialogTitle={{
          title: defaultTexts.cancelSubscription,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        dialogBody={{
          text: defaultTexts.cancelSubscriptionText,
          sx: { fontSize: '18px', color: 'black' }
        }}
        actionButtons={[{
          title: defaultTexts.confirm, 
          onClick: cancelSubscription,
          sx: confirmButtonStyle,
        }]}
        closeIconColor={theme.palette.violetsAreBlue}
      />

      <CustomDialog
        open={updateSubscriptionDialogOpen}
        onClose={handleUpdateSubscriptionDialogClose}
        dialogTitle={{
          title: defaultTexts.updateSubscription,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          }
        }}
        dialogBody={{
          text: defaultTexts.updateSubscriptionText,
          sx: { fontSize: '18px', color: 'black' }
        }}
        actionButtons={[{
          title: defaultTexts.confirm, 
          onClick: updateSubscription,
          sx: confirmButtonStyle,
        }]}
        closeIconColor={theme.palette.violetsAreBlue}
      />

      <CustomDialog
        open={noFundsDialogOpen}
        onClose={handleNoFundsDialogClose}
        dialogTitle={{ title: defaultTexts.noEnoghFunds }}
        dialogBody={{ text: defaultTexts.emptyBalanceNotificationText }}
			/>
    </>
  )
}

export default SubscriptionsViewWrapper